import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import * as FiIcons from 'react-icons/fi';
import SafeIcon from '../common/SafeIcon';
import LogoIcon from '../common/LogoIcon';
import TestimonialCarousel from '../components/TestimonialCarousel';
import ExternalReviewCarousel from '../components/ExternalReviewCarousel';
import SEO from '../components/SEO';
import { ncbService } from '../services/ncbService';

const { FiStar, FiShoppingBag, FiHeart, FiCamera, FiPlus, FiMinus } = FiIcons;

const HERO_PRIORITY_IMAGE = "https://media-manager-c.questera.ai/greta-media/40a492dc43bc618e18a36c285588ccb1d2fe8075e2c19e16c3546447a7641f452c8a03ca0e53bb9227c01b726e9832ee/images/aW1hZ2UvanBlZw==/18bfc2c02a4bcfb5a8c92f1012cd1b04.jpg";
const BOUTIQUE_PRIORITY_IMAGE = "https://media-manager-c.questera.ai/greta-media/40a492dc43bc618e18a36c285588ccb1d2fe8075e2c19e16c3546447a7641f452c8a03ca0e53bb9227c01b726e9832ee/images/aW1hZ2UvanBlZw==/528531fff021bfcdbd42d3cb1260f42b.jpg";

const DEFAULT_FAQS = [
  {
    question: "Is the apartelle accessible for elderly guests?",
    answer: "R. VENUE Apartelle is located on the 3rd floor of the building. Please note that access is via stairs. Our staff is always happy to assist with luggage to make your arrival as comfortable as possible."
  },
  {
    question: "Are you near the Lucban Church and Plaza?",
    answer: "Yes, we are in the absolute center of town! The San Luis Obispo Parish Church and the Town Plaza are just a 2-minute walk from our doorstep."
  },
  {
    question: "What are the check-in and check-out times?",
    answer: "Standard check-in is at 2:00 PM and check-out is at 11:00 AM. Early check-in or late check-out may be available upon request, subject to room availability."
  },
  {
    question: "Is there parking available on-site?",
    answer: "Parking is available at Lavander Building. Please note that this is a paid parking space."
  },
  {
    question: "Do you offer high-speed Wi-Fi?",
    answer: "Absolutely. We offer complimentary high-speed Wi-Fi in all our rooms and common areas, making it perfect for both leisure and remote work."
  },
  {
    question: "Are pets allowed in the rooms?",
    answer: "Pets are not allowed in the room. To maintain a clean and allergy-free environment for all our guests, we currently have a no-pet policy within the apartelle units."
  },
  {
    question: "Do you accept walk-ins or do I need to book in advance?",
    answer: "While we accept walk-ins subject to availability, we highly recommend booking in advance, especially during weekends and the Pahiyas Festival season, to guarantee your stay."
  }
];

const FAQItem = ({ faq, isOpen, toggle }) => {
  return (
    <div className="border-b border-brand-olive/10 last:border-0">
      <button 
        onClick={toggle}
        className="w-full py-6 flex items-center justify-between text-left group"
      >
        <span className="font-serif text-lg md:text-xl text-brand-olive group-hover:text-brand-dark transition-colors pr-8">
          {faq.question || faq.item_title}
        </span>
        <div className={`w-8 h-8 rounded-full border border-brand-olive/20 flex items-center justify-center flex-shrink-0 transition-all duration-300 ${isOpen ? 'bg-brand-olive text-white' : 'bg-transparent text-brand-olive group-hover:border-brand-olive group-hover:scale-110'}`}>
          <SafeIcon icon={isOpen ? FiMinus : FiPlus} className="text-sm" />
        </div>
      </button>
      <AnimatePresence>
        {isOpen && (
          <motion.div
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: 'auto', opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            transition={{ duration: 0.3, ease: 'easeInOut' }}
            className="overflow-hidden"
          >
            <p className="pb-6 text-brand-olive/70 font-light leading-relaxed max-w-3xl">
              {faq.answer || faq.item_description}
            </p>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};

const Home = () => {
  const [openFaqIndex, setOpenFaqIndex] = useState(0);
  const [content, setContent] = useState({ sections: {}, items: {} });

  useEffect(() => {
    const fetchContent = async () => {
      try {
        const sections = await ncbService.getPageSections();
        const items = await ncbService.getPageSectionItems();
        
        const pageSections = sections.filter(s => s.page_slug === 'home').reduce((acc, s) => ({ ...acc, [s.section_key]: s }), {});
        const pageItems = items.filter(i => i.page_slug === 'home').reduce((acc, i) => {
          if (!acc[i.section_key]) acc[i.section_key] = [];
          acc[i.section_key].push(i);
          return acc;
        }, {});

        setContent({ sections: pageSections, items: pageItems });
      } catch (err) {
        console.warn("Failed to load backend content, using fallbacks:", err);
      }
    };
    fetchContent();
  }, []);

  const getSection = (key, fallback) => {
    const fetched = content.sections[key];
    if (!fetched) return fallback;
    const cmsImage = fetched.image_url && typeof fetched.image_url === 'string' && fetched.image_url.trim().length > 0 && fetched.image_url !== 'null' ? fetched.image_url : null;
    return { ...fallback, ...fetched, image_url: cmsImage || fallback.image_url };
  };

  const getItems = (key, fallback) => {
    const fetched = content.items[key];
    return fetched && fetched.length > 0 ? fetched : fallback;
  };

  const hero = {
    ...getSection('hero', {
      title: "R. VENUE", subtitle: "Welcome to", body: "Your relaxing modern retreat in Lucban, Quezon.",
      cta_label: "RESERVE YOUR STAY", cta_url: "/rates", image_url: HERO_PRIORITY_IMAGE 
    }),
    image_url: HERO_PRIORITY_IMAGE
  };

  const intro = getSection('intro', {
    title: "Refined Hospitality", subtitle: "A Peaceful Escape",
    body: "Nestled in the picturesque town of Lucban, R. Venue Apartelle offers a blend of modern elegance and rustic charm. Our rooms are designed to provide the ultimate comfort, making us the perfect choice for travelers seeking a sanctuary amidst nature."
  });

  const lucbanExperience = getSection('lucban_experience', {
    title: "The Lucban Experience", subtitle: "Local Wonders",
    body: "Beyond the comfort of our retreat lies a town rich in culture, flavors, and breathtaking landscapes. Immerse yourself in the authentic charm of Lucban through these curated local experiences."
  });

  const experiences = getItems('experience_items', [
    { icon: FiStar, title: "Pahiyas Spirit", desc: "Celebrate the colorful harvest festival traditions.", link: "/pahiyas-spirit" },
    { icon: FiShoppingBag, title: "Local Finds", desc: "Discover unique handicrafts and delicacies.", link: "/local-finds" },
    { icon: FiHeart, title: "Warm Welcome", desc: "Experience genuine Filipino hospitality.", link: "/warm-welcome" },
    { icon: FiCamera, title: "Scenic Views", desc: "Capture the majesty of Mount Banahaw.", link: "/scenic-views" }
  ]);

  const boutiqueData = content.sections['boutique'] || content.sections['boutique_escape'];
  const boutique = {
    ...getSection(boutiqueData ? (content.sections['boutique'] ? 'boutique' : 'boutique_escape') : 'boutique_escape', {
      title: "Discover the comfort of home with a touch of modern luxury.",
      subtitle: "Your Boutique Escape", cta_label: "Check Availability", cta_url: "/rates", image_url: BOUTIQUE_PRIORITY_IMAGE 
    }),
    image_url: BOUTIQUE_PRIORITY_IMAGE
  };

  const faqs = getItems('faq_items', DEFAULT_FAQS);

  const businessSchema = {
    "@context": "https://schema.org",
    "@type": "LodgingBusiness",
    "name": "R. Venue Apartelle",
    "url": "https://rvenueapartelle.lucban.ph",
    "image": HERO_PRIORITY_IMAGE,
    "description": "Experience premium comfort at R. Venue Apartelle, your cozy sanctuary in the heart of Lucban, Quezon.",
    "address": {
      "@type": "PostalAddress",
      "streetAddress": "3rd Floor R.Venue Bldg., Quezon Ave Cor Avenida Rizal",
      "addressLocality": "Lucban",
      "addressRegion": "Quezon",
      "postalCode": "4328",
      "addressCountry": "PH"
    },
    "telephone": "+63 917 681 8251",
    "email": "rvenuespaces@gmail.com",
    "amenityFeature": [
      { "@type": "LocationFeatureSpecification", "name": "High-speed Wi-Fi", "value": true },
      { "@type": "LocationFeatureSpecification", "name": "Town Center Location", "value": true },
      { "@type": "LocationFeatureSpecification", "name": "Mountain Views", "value": true }
    ]
  };

  return (
    <div className="w-full">
      <SEO 
        description="Experience premium comfort at R. Venue Apartelle, your cozy sanctuary in the heart of Lucban, Quezon. Modern boutique stay in the town center."
        ogImage={HERO_PRIORITY_IMAGE}
        schema={businessSchema}
      />
      
      {/* Hero Section */}
      <section className="relative h-screen min-h-[700px] pt-[90px] w-full overflow-hidden flex items-center justify-center">
        <div className="absolute inset-0 z-0">
          <motion.img 
            src={hero.image_url} 
            alt="Hero Background" 
            className="w-full h-full object-cover animate-slow-zoom"
            onError={(e) => {
              if (e.target.src !== HERO_PRIORITY_IMAGE) {
                e.target.src = HERO_PRIORITY_IMAGE;
              }
            }}
          />
          <div className="absolute inset-0 bg-black/40" />
        </div>

        <div className="relative z-10 flex flex-col items-center justify-center text-center px-6 w-full max-w-5xl mx-auto -mt-10">
          <motion.p initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} className="font-script text-[50px] md:text-[70px] text-brand-cream">{hero.subtitle}</motion.p>
          <motion.h1 initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} className="font-serif text-[60px] sm:text-[90px] md:text-[130px] text-white uppercase tracking-[0.1em] leading-none mb-6 drop-shadow-md">{hero.title}</motion.h1>
          <motion.p initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} className="text-white text-lg md:text-[22px] font-sans tracking-wide font-light">{hero.body}</motion.p>
          <div className="max-w-[600px] h-[1px] bg-white/40 mt-8 mb-10" />
          <Link to={hero.cta_url || '/rates'} className="inline-block px-10 py-4 bg-brand-olive text-white font-bold uppercase tracking-widest text-xs transition-all duration-300 shadow-xl hover:bg-brand-dark hover:scale-105 hover:shadow-2xl active:scale-95">
            {hero.cta_label}
          </Link>
        </div>
      </section>

      {/* Intro Section */}
      <section className="py-24 bg-brand-cream">
        <div className="container mx-auto px-6 text-center max-w-3xl">
          <LogoIcon className="w-24 h-24 mx-auto mb-8 shadow-xl rounded-full bg-white transition-transform duration-500 hover:rotate-[360deg] hover:scale-110" />
          <p className="font-script text-3xl text-brand-olive mb-2">{intro.subtitle}</p>
          <h2 className="font-serif text-3xl md:text-4xl uppercase tracking-widest text-brand-olive mb-6">{intro.title}</h2>
          <div className="w-20 h-[1px] bg-brand-olive mx-auto mb-8" />
          <p className="text-brand-olive text-lg leading-relaxed font-light">{intro.body}</p>
        </div>
      </section>

      {/* Promotional Video Section */}
      <section className="py-24 bg-white">
        <div className="container mx-auto px-6 max-w-5xl text-center">
          <motion.div initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} className="mb-14">
            <h2 className="font-serif text-3xl md:text-4xl uppercase tracking-widest text-brand-olive mb-6">Experience R. Venue Apartelle</h2>
            <div className="w-20 h-[1px] bg-brand-olive mx-auto mb-10" />
            <div className="space-y-8 max-w-3xl mx-auto">
              <p className="text-brand-olive text-lg md:text-xl leading-relaxed font-light">
                Step inside R. Venue Apartelle and discover the quiet charm, relaxing spaces, and warm hospitality that make every stay feel personal.
              </p>
            </div>
          </motion.div>
          <motion.div initial={{ opacity: 0, scale: 0.95 }} whileInView={{ opacity: 1, scale: 1 }} viewport={{ once: true }} transition={{ delay: 0.2 }} className="relative aspect-video w-full rounded-2xl overflow-hidden shadow-[0_20px_50px_rgba(0,0,0,0.15)] border border-brand-olive/5 bg-brand-cream/50">
            <iframe className="absolute inset-0 w-full h-full" src="https://www.youtube.com/embed/Rz-jfn49x2w?si=oJg37BEfGivwmQn3" title="Experience R. Venue Apartelle" frameBorder="0" allow="accelerometer; clipboard-write; encrypted-media; gyroscope; picture-in-picture" allowFullScreen loading="lazy"></iframe>
          </motion.div>
          <motion.div initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ delay: 0.4 }} className="mt-16">
            <Link to="/rates" className="inline-block px-10 py-4 bg-brand-olive text-white font-bold uppercase tracking-widest text-xs transition-all duration-300 shadow-xl hover:bg-brand-dark hover:scale-105 hover:shadow-2xl active:scale-95">Reserve Your Stay</Link>
          </motion.div>
        </div>
      </section>

      {/* Lucban Experience Grid */}
      <section className="py-24 bg-brand-olive text-white">
        <div className="container mx-auto px-6 text-center mb-16">
          <p className="font-script text-3xl text-brand-cream mb-2">{lucbanExperience.subtitle}</p>
          <h2 className="font-serif text-3xl md:text-4xl uppercase tracking-widest text-white">{lucbanExperience.title}</h2>
          <div className="w-20 h-[1px] bg-white/30 mx-auto mt-6 mb-8" />
          <p className="text-brand-cream/80 text-lg leading-relaxed font-light max-w-2xl mx-auto">{lucbanExperience.body}</p>
        </div>
        <div className="container mx-auto px-6 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-8">
          {experiences.map((item, idx) => (
            <Link to={item.link || item.link_url} key={idx} className="block group">
              <motion.div className="bg-brand-cream p-8 shadow-lg text-center h-full flex flex-col items-center justify-center group-hover:bg-white group-hover:scale-[1.05] transition-all duration-500 border border-brand-olive/5 cursor-pointer">
                <div className="w-12 h-12 bg-brand-olive/10 rounded-full flex items-center justify-center mx-auto mb-6 group-hover:bg-brand-olive transition-colors duration-500">
                  <SafeIcon icon={item.icon || (FiIcons[item.icon_name] || FiStar)} className="text-brand-olive group-hover:text-white text-xl transition-transform duration-500 group-hover:rotate-12" />
                </div>
                <h3 className="font-serif text-lg uppercase tracking-widest text-brand-olive mb-4">{item.title || item.item_title}</h3>
                <p className="text-brand-olive/80 text-sm leading-relaxed">{item.desc || item.item_description}</p>
              </motion.div>
            </Link>
          ))}
        </div>
      </section>

      {/* Testimonials Section */}
      <section className="py-24 bg-brand-cream overflow-hidden">
        <div className="container mx-auto px-6">
          <div className="text-center mb-16">
            <p className="font-script text-3xl text-brand-olive mb-2">Guest Stories</p>
            <h2 className="font-serif text-3xl md:text-4xl uppercase tracking-widest text-brand-olive">Kind Words From Our Guests</h2>
            <div className="w-20 h-[1px] bg-brand-olive mx-auto mt-6" />
          </div>
          <TestimonialCarousel />
        </div>
      </section>

      {/* Guest Reviews Carousel Section */}
      <section className="py-24 bg-white border-y border-brand-olive/5">
        <div className="container mx-auto px-6">
          <div className="text-center mb-16">
            <p className="font-script text-3xl text-brand-olive mb-2">Verified Feedback</p>
            <h2 className="font-serif text-3xl md:text-4xl uppercase tracking-widest text-brand-olive">Guest Reviews</h2>
            <div className="w-20 h-[1px] bg-brand-olive/20 mx-auto mt-6" />
          </div>
          <ExternalReviewCarousel />
        </div>
      </section>

      {/* Boutique Escape Section */}
      <section className="bg-brand-olive">
        <div className="grid grid-cols-1 md:grid-cols-2">
          <div className="h-[400px] md:h-auto w-full relative overflow-hidden group">
            <img src={boutique.image_url} alt="R. Venue Room" className="w-full h-full object-cover transition-transform duration-1000 group-hover:scale-110" onError={(e) => { if (e.target.src !== BOUTIQUE_PRIORITY_IMAGE) { e.target.src = BOUTIQUE_PRIORITY_IMAGE; } }} />
            <div className="absolute inset-0 bg-black/10 group-hover:bg-transparent transition-colors duration-700" />
          </div>
          <div className="py-10 md:py-12 px-10 md:px-16 lg:px-24 flex flex-col justify-center items-start bg-brand-olive relative">
            <p className="font-script text-3xl text-brand-cream mb-4">{boutique.subtitle}</p>
            <h2 className="font-serif text-3xl md:text-4xl lg:text-5xl uppercase tracking-[0.1em] mb-8 leading-tight text-white">{boutique.title}</h2>
            <Link to={boutique.cta_url || '/rates'} className="inline-block px-12 py-5 bg-white text-brand-olive font-bold uppercase tracking-[0.2em] text-xs transition-all duration-500 shadow-[0_10px_30px_rgba(0,0,0,0.2)] hover:bg-white hover:scale-110 hover:shadow-[0_20px_40px_rgba(0,0,0,0.3)] active:scale-95 transform-gpu">
              {boutique.cta_label}
            </Link>
          </div>
        </div>
      </section>

      {/* FAQ Section */}
      <section className="py-24 bg-brand-cream">
        <div className="container mx-auto px-6 max-w-4xl">
          <div className="text-center mb-16">
            <p className="font-script text-3xl text-brand-olive mb-2">Common Inquiries</p>
            <h2 className="font-serif text-3xl md:text-4xl uppercase tracking-widest text-brand-olive">Frequently Asked Questions</h2>
            <div className="w-20 h-[1px] bg-brand-olive mx-auto mt-6" />
          </div>
          <div className="bg-white p-8 md:p-12 shadow-2xl border border-brand-olive/5">
            {faqs.map((faq, idx) => (
              <FAQItem key={idx} faq={faq} isOpen={openFaqIndex === idx} toggle={() => setOpenFaqIndex(openFaqIndex === idx ? -1 : idx)} />
            ))}
          </div>
        </div>
      </section>
    </div>
  );
};

export default Home;